import React, { useState, useRef } from 'react';
import { Box, Card, Flex } from 'rebass';
import {
  Ani,
  ANI_TIME,
  QuizContainer,
  CONTAINER_PADDING,
  MAX_WIDTH,
  BACKGROUND_COLOR,
} from '../common';
import { IQuiz, ElementType, ICode } from '../type';
import { playPassAnimation, playFailAnimation } from '../animation';
import Code from './Code';

interface IProps {
  show: boolean;
  data: IQuiz;
  onFail: () => void;
  onNext: () => void;
  increaseStar: () => void;
}

function Quiz({ data, show, onFail, onNext, increaseStar }: IProps) {
  const [aniPass, setAniPass] = useState(0);
  const [aniFail, setAniFail] = useState(0);
  const toRef = useRef<HTMLElement>(null);
  const fromRef = useRef<HTMLElement>(null);
  const failRef = useRef<HTMLElement>(null);
  const passRef = useRef<HTMLElement>(null);

  function onClick(e: React.SyntheticEvent<HTMLElement>) {
    playPassAnimation(e);
    setAniPass(1);
    setTimeout(() => {
      setAniPass(2);
      if (passRef.current && fromRef.current) {
        const boundFrom = fromRef.current.getBoundingClientRect();
        passRef.current.style.left = `${boundFrom.left}px`;
        passRef.current.style.top = `${boundFrom.top + window.scrollY}px`;
        passRef.current.style.width = `${boundFrom.width}px`;
        passRef.current.textContent = fromRef.current.textContent || '';
        setTimeout(() => {
          if (passRef.current && toRef.current) {
            const boundTo = toRef.current.getBoundingClientRect();
            passRef.current.style.left = `${boundTo.left}px`;
            passRef.current.style.top = `${boundTo.top + window.scrollY}px`;
          }
          increaseStar();
        }, ANI_TIME);
      }
    }, 1);
    setTimeout(() => {
      setAniPass(3);
    }, ANI_TIME * 2);
    setTimeout(() => {
      onNext();
    }, ANI_TIME * 3);
  }

  function onClickFail(e: React.MouseEvent) {
    setAniFail(1);
    if (failRef.current) {
      failRef.current.style.left = `${e.pageX - FAIL_TEXT_WIDTH / 2}px`;
      failRef.current.style.top = `${e.pageY - FAIL_TEXT_HEIGHT / 2}px`;
      const index = Math.floor(Math.random() * 10 - 0.001);
      const icon = ICON_LIST[index];
      failRef.current.textContent = icon + icon + icon;
    }
    setTimeout(() => {
      onFail();
    }, ANI_TIME * 3);
    playFailAnimation(e);
  }

  const options = data.filter(
    item =>
      item.type === ElementType.OptionPass ||
      item.type === ElementType.OptionFail,
  );
  const description = data.filter(
    item =>
      item.type !== ElementType.OptionPass &&
      item.type !== ElementType.OptionFail,
  );

  return (
    <>
      <QuizContainer style={{ opacity: show && !aniFail ? 1 : 0 }}>
        <Box>
          {description.map((item, index) => (
            <React.Fragment key={index}>
              {item.type === ElementType.NewLine && (
                <Box mb={item.data as string} />
              )}
              {item.type === ElementType.TextCode && (
                <Code data={item.data as ICode} />
              )}
              {item.type === ElementType.Text && item.data}
              {item.type === ElementType.TextHide && !aniPass && item.data}
              {item.type === ElementType.TextShowLast && (
                <Ani show={aniPass === 3}>{item.data}</Ani>
              )}
              {item.type === ElementType.TextMoveTo && (
                <Ani ref={toRef} show={aniPass === 3}>
                  {item.data}
                </Ani>
              )}
            </React.Fragment>
          ))}
        </Box>
        <Card
          width="100%"
          p="20px"
          my="50px"
          bg={aniPass ? BACKGROUND_COLOR : '#111'}
          borderRadius={8}
          style={{ maxWidth: MAX_WIDTH - CONTAINER_PADDING * 2 }}
        >
          {options.map((item, index) => (
            <React.Fragment key={index}>
              <Flex
                onClick={
                  item.type === ElementType.OptionFail ? onClickFail : onClick
                }
                style={{ cursor: 'pointer' }}
              >
                <Ani
                  show={!aniPass}
                  style={{ color: '#a6e22e', flex: '0 0 22px' }}
                >{`${index + 1}) `}</Ani>
                <Ani
                  ref={
                    item.type === ElementType.OptionFail ? undefined : fromRef
                  }
                  show={!aniPass}
                >
                  {item.data}
                </Ani>
              </Flex>
              {index < options.length - 1 && <Box mb="15px" />}
            </React.Fragment>
          ))}
        </Card>
      </QuizContainer>
      <Ani
        ref={failRef}
        show={!!aniFail}
        style={{
          color: 'red',
          position: 'absolute',
          fontSize: '40px',
          width: `${FAIL_TEXT_WIDTH}px`,
          height: `${FAIL_TEXT_HEIGHT}px`,
          textAlign: 'center',
        }}
      />
      <Ani
        ref={passRef}
        show={aniPass === 2}
        style={{
          position: 'absolute',
        }}
      />
    </>
  );
}

const FAIL_TEXT_WIDTH = 300;
const FAIL_TEXT_HEIGHT = 50;
const ICON_LIST = ['😓', '😩', '😪', '😭', '😰', '😱', '😡', '😈', '😵', '😅'];

export default Quiz;
